import 'neuicons';
import './index.css';
import 'focus-visible';
import './analytics/initAnalytics';

import MomentUtils from '@date-io/moment';
import { ThemeProvider } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ThemeProvider as ThemeProviderV6 } from '@mui/material';
import ReachPortal from '@reach/portal';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter } from 'react-router-dom';

import { AppSunset } from '@/components/AppSunset';
import { ErrorBoundary as ErrorBoundaryImpl } from '@/components/ErrorBoundaries/ErrorBoundary';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { isNativeShell, isShellPlatform } from '@/utils/native-shell';

import {
  getBrandingConfig,
  updateBrandingTitleAndFavicon,
} from './branding/config';
import { AuthenticationStatusListener } from './components/AuthenticationStatusListener';
import { AuthProvider } from './components/AuthProvider';
import { queryClient } from './query-client';
import App from './routes';
import { theme } from './theme/theme';
import { themeV6 } from './theme/themeV6';
import { isIcra, isPWC } from './utils/isEnv';

// Set request origin for this app
sessionStorage.setItem('request-origin', 'FRONTEND_REACT');

// Handle branding at module initialization level
const branding = getBrandingConfig();
updateBrandingTitleAndFavicon(branding.tabTitle, branding.faviconHref);

if (isPWC()) {
  import('./pwc.css');
}

if (isIcra()) {
  import('./icra.css');
}

async function prepare() {
  if (import.meta.env.DEV) {
    return import('./mocks/browser').then(({ default: mocks }) => {
      return mocks.start({ onUnhandledRequest: 'bypass' });
    });
  }
}

const Main = () => {
  const { mobile } = useResponsiveConstants();

  React.useEffect(() => {
    window.process.env = window.process.env || {};
    window.process.env.CDN_DOMAIN = import.meta.env.VITE_CDN_DOMAIN;
    window.process.env.WEB_URL = import.meta.env.VITE_WEB_URL;
    window.process.env.HOST_ENV = import.meta.env.VITE_HOST_ENV;

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <ThemeProviderV6 theme={themeV6}>
            <QueryClientProvider client={queryClient}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <App />
                <AuthenticationStatusListener />
                <ReachPortal>
                  <Toaster
                    position='bottom-center'
                    reverseOrder={false}
                    toastOptions={{
                      duration: 8000,
                      ...(mobile && { style: { marginBottom: '70px' } }),
                      className: 'font-medium',
                      style: {
                        backgroundColor: 'var(--gray-900)',
                        color: 'white',
                      },
                    }}
                  />
                </ReachPortal>
              </MuiPickersUtilsProvider>
              {import.meta.env.DEV ? (
                <ReactQueryDevtools initialIsOpen={false} />
              ) : null}
            </QueryClientProvider>
          </ThemeProviderV6>
        </ThemeProvider>
      </BrowserRouter>
    </AuthProvider>
  );
};

const Root = () => {
  const [nativeShell, setNativeShell] = React.useState(isNativeShell);

  React.useEffect(() => {
    if (nativeShell) {
      return;
    }

    // the shell dispatches this on document, never on window -- listening on
    // window would let any embedded iframe blank the app for browser users
    const handleShellHandshake = (message: MessageEvent) => {
      if (isShellPlatform(message.data?.device)) {
        setNativeShell(true);
      }
    };

    document.addEventListener('message', handleShellHandshake as EventListener);

    return () => {
      document.removeEventListener(
        'message',
        handleShellHandshake as EventListener
      );
    };
  }, [nativeShell]);

  return nativeShell ? <AppSunset /> : <Main />;
};

prepare().then(() => {
  ReactDOM.render(
    <ErrorBoundary FallbackComponent={ErrorBoundaryImpl}>
      <Root />
    </ErrorBoundary>,
    document.getElementById('root')
  );
});
